import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { SharedAlert } from "../../../app/siteSlice";
import AlertMessage, { AlertLevel } from "../../../components/AlertMessage";
import PromptLibrary from "../../prompts/PromptLibrary";
import CourseWritingActivity from "./CourseWritingActivity";

export enum TeacherWritingSubTabs {
	WritingActivity = "Writing Activity",
	PromptLibrary = "Prompt Library",
}

const initialSubTabs = [
	{ name: TeacherWritingSubTabs.WritingActivity, current: true },
	{ name: TeacherWritingSubTabs.PromptLibrary, current: false },
];

export default function TeacherWriting() {
	const [tabs, setTabs] = useState(initialSubTabs);
	const [currentTab, setCurrentTab] = useState(initialSubTabs[0].name);

	const [alert, setAlert] = useState<SharedAlert>({ level: AlertLevel.Warning, message: "" });
	const [selectedPromptId, setSelectedPromptId] = useState<number>();
	const [searchFilter, setSearchFilter] = useState("");
	const [sortField, setSortField] = useState<string>();
	const [expandedStatusFilter, setExpandedStatusFilter] = useState<string>("all");

	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		if (location.state != null) {
			setAlert({ level: AlertLevel.Error, message: location.state.errorMessage });
			setSearchFilter(location.state.promptName);
			setSelectedPromptId(location.state.promptId);
			setSortField(location.state.sortField);
			setExpandedStatusFilter(location.state.expandedStatus ?? "all");
			if (location.state.subTab) handleSubTabChange(location.state.subTab);
		}
		navigate(location, { replace: true });
	}, []);

	function handleSubTabChange(name: TeacherWritingSubTabs): void {
		setTabs(
			tabs.map((tab) =>
				tab.name === name ? { ...tab, current: true } : { ...tab, current: false }
			)
		);
		setCurrentTab(name);
	}

	return (
		<>
			<section>
				<h1>Writing</h1>
				<p className="mt-4">
					Welcome to the Writing Hub! From this page, you can view your course Writing
					Activity. You can click on a prompt in the list to see more detailed progress
					and click on a specific student’s writing. To see all prompt options, click on
					the Prompt Library, where you can edit and assign prepackaged prompts or create
					and assign your own prompt.
				</p>
			</section>
			<div className="w-full">
				<div className="mt-8 px-4">
					<div
						aria-label="Activity Tabs"
						className="flex divide-x divide-gray-200 rounded-lg shadow-sm items-center">
						{tabs.map((tab, tabIdx) => (
							<button
								onClick={() => handleSubTabChange(tab.name)}
								key={tab.name}
								aria-current={tab.current ? "page" : undefined}
								className={`${
									tab.current
										? "text-gray-900 !text-lg bg-gray-100 border-b-2 border-b-[var(--subtab-border)]"
										: "text-gray-500 hover:text-gray-700 border-b !border-b-transparent hover:bg-gray-100"
								} 
									${tabIdx === 0 ? "rounded-l-lg" : tabIdx === tabs.length - 1 ? "rounded-r-lg" : ""} 
									group flex-1 relative p-4 text-center text-sm font-medium hover:cursor-pointer focus:z-10`}>
								<span>{tab.name}</span>
							</button>
						))}
					</div>
					<div className="mt-4">
						{currentTab === TeacherWritingSubTabs.WritingActivity && (
							<CourseWritingActivity
								selectedPromptId={selectedPromptId}
								setSelectedPromptId={setSelectedPromptId}
								sortField={sortField}
								expandedStatusFilter={expandedStatusFilter}
								setExpandedStatusFilter={setExpandedStatusFilter}
							/>
						)}
						{currentTab === TeacherWritingSubTabs.PromptLibrary && (
							<section>
								{alert.message && (
									<AlertMessage
										className="mt-4 text-sm"
										type={alert.level}
										message={alert.message}
										showIcon={true}
										onClear={() => setAlert({ ...alert, message: "" })}
									/>
								)}
								<PromptLibrary
									selectedPromptId={selectedPromptId}
									setSelectedPromptId={setSelectedPromptId}
									searchFilter={searchFilter}
									setAlert={setAlert}
								/>
							</section>
						)}
					</div>
				</div>
			</div>
		</>
	);
}
